import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { PurchaseService } from '../../../services/purchaseService';
import { productService } from '../../../services/productService';
import { formatCurrency } from '../../../utils/formatters';

interface BillItem {
  id?: string;
  variantId: string;
  productName: string;
  variantName: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
}

interface PurchaseBillFormData {
  billNumber: string;
  purchaseOrderId?: string;
  supplierId: string;
  billDate: string;
  dueDate: string;
  supplierInvoiceNumber: string;
  supplierInvoiceDate: string;
  paymentTerms: string;
  shippingCharges: number;
  packingCharges: number;
  otherCharges: Array<{ name: string; amount: number }>;
  notes: string;
  items: BillItem[];
}

export const PurchaseBillForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Check if converting from PO
  const purchaseOrderId = new URLSearchParams(location.search).get('po_id');
  const isConvertingFromPO = Boolean(purchaseOrderId);

  const [formData, setFormData] = useState<PurchaseBillFormData>({
    billNumber: '',
    purchaseOrderId: purchaseOrderId || undefined,
    supplierId: '',
    billDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    supplierInvoiceNumber: '',
    supplierInvoiceDate: format(new Date(), 'yyyy-MM-dd'),
    paymentTerms: 'Net 30',
    shippingCharges: 0,
    packingCharges: 0,
    otherCharges: [],
    notes: '',
    items: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<any>(null);

  // Fetch initial data
  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    if (isEditing && id) {
      fetchBill(id);
    } else if (isConvertingFromPO && purchaseOrderId) {
      fetchPurchaseOrder(purchaseOrderId);
    }
  }, [id, purchaseOrderId]);

  const fetchSuppliers = async () => {
    try {
      const response: any = await PurchaseService.getSuppliers({ page: 1, limit: 100 });
      setSuppliers(response.items || response.suppliers || response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response: any = await productService.listProducts({ page: 1, per_page: 100 });
      setProducts(response.products || response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchBill = async (billId: string) => {
    try {
      setLoading(true);
      const bill = await PurchaseService.getPurchaseBill(billId);
      
      setFormData({
        billNumber: bill.billNumber,
        purchaseOrderId: bill.purchaseOrderId,
        supplierId: bill.supplierId,
        billDate: bill.billDate,
        dueDate: bill.dueDate || '',
        supplierInvoiceNumber: (bill as any).supplierInvoiceNumber || '',
        supplierInvoiceDate: (bill as any).supplierInvoiceDate || '',
        paymentTerms: (bill as any).paymentTerms || 'Net 30',
        shippingCharges: Number(bill.shippingCharges) || 0,
        packingCharges: Number(bill.packingCharges) || 0,
        otherCharges: (bill.otherCharges || []).map((c: any) => ({ name: c.chargeName || c.name || '', amount: c.amount || 0 })),
        notes: (bill as any).notes || '',
        items: ((bill as any).items || []).map((item: any) => ({
          id: item.id,
          variantId: item.variantId,
          productName: item.variant?.product?.name || '',
          variantName: item.variant?.name || '',
          quantity: item.quantity,
          unitPrice: Number(item.unitPrice),
          discountPercent: Number(item.discountPercent) || 0,
          taxRate: Number(item.taxRate) || 18,
          taxAmount: Number(item.taxAmount),
          lineTotal: Number(item.lineTotal),
        })),
      });

      const supplier = suppliers.find((s) => s.id === bill.supplierId);
      setSelectedSupplier(supplier);
    } catch (error) {
      console.error('Failed to fetch bill:', error);
      enqueueSnackbar('Failed to load bill', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrder = async (poId: string) => {
    try {
      setLoading(true);
      const po = await PurchaseService.getPurchaseOrder(poId);
      setPurchaseOrder(po);

      setFormData((prev) => ({
        ...prev,
        purchaseOrderId: po.id,
        supplierId: po.supplierId,
        billNumber: `BILL-${po.poNumber}`,
        items: po.items?.map((item: any) => ({
          variantId: item.variantId,
          productName: item.variant?.product?.name || '',
          variantName: item.variant?.name || '',
          quantity: item.quantity,
          unitPrice: Number(item.unitPrice),
          discountPercent: Number(item.discountPercent) || 0,
          taxRate: Number(item.taxRate) || 18,
          taxAmount: 0,
          lineTotal: 0,
        })) || [],
      }));

      const supplier = suppliers.find((s) => s.id === po.supplierId);
      setSelectedSupplier(supplier);
    } catch (error) {
      console.error('Failed to fetch purchase order:', error);
      enqueueSnackbar('Failed to load purchase order', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Calculate line item totals
  const calculateLineTotal = (item: BillItem): BillItem => {
    const lineAmount = item.quantity * item.unitPrice;
    const discountAmount = lineAmount * (item.discountPercent / 100);
    const taxableAmount = lineAmount - discountAmount;
    const taxAmount = taxableAmount * (item.taxRate / 100);
    const lineTotal = taxableAmount + taxAmount;

    return {
      ...item,
      taxAmount,
      lineTotal,
    };
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsSubtotal = formData.items.reduce((sum, item) => {
      const lineAmount = item.quantity * item.unitPrice;
      const discountAmount = lineAmount * (item.discountPercent / 100);
      return sum + (lineAmount - discountAmount);
    }, 0);

    const itemsTax = formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
    
    const chargesAmount = 
      formData.shippingCharges + 
      formData.packingCharges + 
      formData.otherCharges.reduce((sum, charge) => sum + charge.amount, 0);
    
    const chargesTax = chargesAmount * 0.18; // Assuming 18% GST on charges
    
    const subtotal = itemsSubtotal;
    const totalTax = itemsTax + chargesTax;
    const grandTotal = subtotal + totalTax + chargesAmount;

    return {
      subtotal,
      totalTax,
      chargesAmount,
      grandTotal,
    };
  };

  const totals = calculateTotals();

  // Handle form changes
  const handleChange = (field: keyof PurchaseBillFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSupplierChange = (supplier: any) => {
    setSelectedSupplier(supplier);
    handleChange('supplierId', supplier?.id || '');
  };

  const handleAddItem = () => {
    const newItem: BillItem = {
      variantId: '',
      productName: '',
      variantName: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      taxRate: 18,
      taxAmount: 0,
      lineTotal: 0,
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index: number, field: keyof BillItem, value: any) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      newItems[index] = calculateLineTotal(newItems[index]);
      return { ...prev, items: newItems };
    });
  };

  const handleProductSelect = (index: number, product: any) => {
    if (!product) return;

    const variant = product.variants?.[0];
    if (!variant) return;

    handleItemChange(index, 'variantId', variant.id);
    handleItemChange(index, 'productName', product.name);
    handleItemChange(index, 'variantName', variant.name);
    handleItemChange(index, 'unitPrice', Number(variant.purchasePrice || variant.price || 0));
  };

  const handleAddCharge = () => {
    setFormData((prev) => ({
      ...prev,
      otherCharges: [...prev.otherCharges, { name: '', amount: 0 }],
    }));
  };

  const handleRemoveCharge = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      otherCharges: prev.otherCharges.filter((_, i) => i !== index),
    }));
  };

  const handleChargeChange = (index: number, field: 'name' | 'amount', value: any) => {
    setFormData((prev) => {
      const newCharges = [...prev.otherCharges];
      newCharges[index] = { ...newCharges[index], [field]: value };
      return { ...prev, otherCharges: newCharges };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierId) newErrors.supplierId = 'Supplier is required';
    if (!formData.billNumber) newErrors.billNumber = 'Bill number is required';
    if (!formData.billDate) newErrors.billDate = 'Bill date is required';
    if (!formData.supplierInvoiceNumber) newErrors.supplierInvoiceNumber = 'Supplier invoice number is required';
    if (formData.items.length === 0) newErrors.items = 'At least one item is required';

    formData.items.forEach((item, index) => {
      if (!item.variantId) newErrors[`item_${index}_variant`] = 'Product is required';
      if (item.quantity <= 0) newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      if (item.unitPrice < 0) newErrors[`item_${index}_price`] = 'Price cannot be negative';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      enqueueSnackbar('Please fix the errors', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);

      const billData: any = {
        billNumber: formData.billNumber,
        purchaseOrderId: formData.purchaseOrderId,
        supplierId: formData.supplierId,
        billDate: formData.billDate,
        dueDate: formData.dueDate,
        supplierInvoiceNumber: formData.supplierInvoiceNumber,
        supplierInvoiceDate: formData.supplierInvoiceDate,
        paymentTerms: formData.paymentTerms,
        shippingCharges: formData.shippingCharges,
        packingCharges: formData.packingCharges,
        otherCharges: formData.otherCharges,
        notes: formData.notes,
        items: formData.items.map((item) => ({
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          taxRate: item.taxRate,
        })),
      };

      if (isEditing && id) {
        await PurchaseService.updatePurchaseBill(id, billData);
        enqueueSnackbar('Bill updated successfully', { variant: 'success' });
      } else {
        const newBill = await PurchaseService.createPurchaseBill(billData);
        enqueueSnackbar('Bill created successfully', { variant: 'success' });
        navigate(`/admin/purchase/bills/${newBill.id}`);
        return;
      }

      navigate(`/admin/purchase/bills/${id}`);
    } catch (error: any) {
      console.error('Failed to save bill:', error);
      enqueueSnackbar(error.response?.data?.detail || 'Failed to save bill', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.items.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/purchase/bills')}>
            Back
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Purchase Bill' : 'Create Purchase Bill'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Bill'}
        </Button>
      </Box>

      {isConvertingFromPO && purchaseOrder && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Converting from Purchase Order: {purchaseOrder.poNumber}
        </Alert>
      )}

      {errors.items && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.items}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Bill Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bill Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option) => option.companyName || option.name || ''}
                    value={selectedSupplier}
                    onChange={(_, value) => handleSupplierChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier *"
                        error={Boolean(errors.supplierId)}
                        helperText={errors.supplierId}
                      />
                    )}
                    disabled={isConvertingFromPO}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bill Number *"
                    value={formData.billNumber}
                    onChange={(e) => handleChange('billNumber', e.target.value)}
                    error={Boolean(errors.billNumber)}
                    helperText={errors.billNumber}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bill Date *"
                    type="date"
                    value={formData.billDate}
                    onChange={(e) => handleChange('billDate', e.target.value)}
                    error={Boolean(errors.billDate)}
                    helperText={errors.billDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleChange('dueDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Payment Terms"
                    value={formData.paymentTerms}
                    onChange={(e) => handleChange('paymentTerms', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Supplier Invoice Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Invoice Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Supplier Invoice Number *"
                    value={formData.supplierInvoiceNumber}
                    onChange={(e) => handleChange('supplierInvoiceNumber', e.target.value)}
                    error={Boolean(errors.supplierInvoiceNumber)}
                    helperText={errors.supplierInvoiceNumber}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Supplier Invoice Date"
                    type="date"
                    value={formData.supplierInvoiceDate}
                    onChange={(e) => handleChange('supplierInvoiceDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Items */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Items</Typography>
                <Button startIcon={<AddIcon />} onClick={handleAddItem} variant="outlined">
                  Add Item
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="30%">Product</TableCell>
                      <TableCell width="10%">Quantity</TableCell>
                      <TableCell width="15%">Unit Price</TableCell>
                      <TableCell width="10%">Discount %</TableCell>
                      <TableCell width="10%">Tax %</TableCell>
                      <TableCell width="15%" align="right">Line Total</TableCell>
                      <TableCell width="5%"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Autocomplete
                            options={products}
                            getOptionLabel={(option) => option.name || ''}
                            onChange={(_, value) => handleProductSelect(index, value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={Boolean(errors[`item_${index}_variant`])}
                                helperText={errors[`item_${index}_variant`]}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity || ''}
                            onChange={(e) =>
                              handleItemChange(index, 'quantity', Number(e.target.value))
                            }
                            error={Boolean(errors[`item_${index}_quantity`])}
                            inputProps={{ min: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.unitPrice || ''}
                            onChange={(e) =>
                              handleItemChange(index, 'unitPrice', Number(e.target.value))
                            }
                            error={Boolean(errors[`item_${index}_price`])}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.discountPercent || ''}
                            onChange={(e) =>
                              handleItemChange(index, 'discountPercent', Number(e.target.value))
                            }
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.taxRate}
                            onChange={(e) =>
                              handleItemChange(index, 'taxRate', Number(e.target.value))
                            }
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrency(item.lineTotal)}</Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Charges and Totals */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Charges
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Shipping Charges"
                    type="number"
                    value={formData.shippingCharges}
                    onChange={(e) => handleChange('shippingCharges', Number(e.target.value))}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Packing Charges"
                    type="number"
                    value={formData.packingCharges}
                    onChange={(e) => handleChange('packingCharges', Number(e.target.value))}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Other Charges</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddCharge}>
                      Add
                    </Button>
                  </Box>
                  {formData.otherCharges.map((charge, index) => (
                    <Box key={index} display="flex" gap={1} mb={1}>
                      <TextField
                        size="small"
                        placeholder="Charge name"
                        value={charge.name}
                        onChange={(e) => handleChargeChange(index, 'name', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Amount"
                        value={charge.amount}
                        onChange={(e) =>
                          handleChargeChange(index, 'amount', Number(e.target.value))
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 120 }}
                      />
                      <IconButton size="small" onClick={() => handleRemoveCharge(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bill Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">Subtotal:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {formatCurrency(totals.subtotal)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Tax:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {formatCurrency(totals.totalTax)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Charges:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {formatCurrency(totals.chargesAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">Grand Total:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" align="right">
                    {formatCurrency(totals.grandTotal)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add any additional notes..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
